// frontend/src/utils/tableExport.js
//
// v2.5.153 — shared table-export utility used by Stats and Finance pages.
//
// Three formats, one input shape:
//   exportTable({ filename, title, columns, rows, format })
//     • format: 'csv' | 'xlsx' | 'pdf'
//     • columns: [{ key, label, align? }]
//     • rows: array of objects keyed by column.key
//     • title: shown on PDF top and as the XLSX sheet name
//     • filename: stem (no extension); the extension is appended per format
//
// Notes:
//   • XLSX uses write-excel-file (2026-10-01; was SheetJS 0.18.5, which is
//     abandoned on npm with open advisories). See xlsxBlob() below.
//   • PDF uses jsPDF + jspdf-autotable for clean tabular output with
//     auto-paging, headers, and basic styling. Both are added as deps.
//   • CSV is implemented inline (RFC4180 quoting) — small, no dep.
//   • All three trigger a real browser download so coaches get a file
//     they can keep / forward without a server round-trip.

// 2026-07-17 (build-report INEFFECTIVE_DYNAMIC_IMPORT fix) — xlsx is now
// loaded LAZILY inside exportXlsx(), matching how exportPdf() already
// treats jsPDF and how ImportPlayersPage lazy-loads xlsx. This static
// import was the one eager consumer that dragged the entire SheetJS
// library (~400 KB min) into the main bundle for every visitor, defeating
// the dynamic imports elsewhere. Nobody pays for xlsx until they actually
// click an XLSX export.

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const SAFE_NAME = (s) => String(s || 'export').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 80);

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Slight delay before revoke so Safari fully starts the download.
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

// RFC4180-compliant CSV cell quoting.
function csvCell(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toCsv(columns, rows) {
  const header = columns.map(c => csvCell(c.label)).join(',');
  const body   = rows.map(r => columns.map(c => csvCell(r[c.key])).join(',')).join('\n');
  // BOM so Excel opens UTF-8 correctly on Windows.
  return '\uFEFF' + header + '\n' + body + '\n';
}

function exportCsv({ filename, columns, rows }) {
  const text = toCsv(columns, rows);
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `${SAFE_NAME(filename)}_${today()}.csv`);
}

// Build an .xlsx Blob from an array of rows (first row = headers).
// write-excel-file's "universal" build zips with fflate on the main thread:
// no Web Worker, no Node polyfills. Header row is bold and stays frozen when
// scrolling. Empty cells must be null; numbers stay numbers so Excel can
// sort and sum them; Date objects become real dates. Strings are always
// written as text (never formulas).
export async function xlsxBlob({ sheet, rows, widths }) {
  const { default: writeExcelFile } = await import('write-excel-file/universal');
  const [header = [], ...body] = rows;
  const data = [
    header.map(h => ({ value: String(h ?? ''), fontWeight: 'bold' })),
    ...body.map(r => r.map(v => (v == null || v === '') ? null
      : (typeof v === 'number' || typeof v === 'boolean' || v instanceof Date) ? v : String(v))),
  ];
  const name = String(sheet || 'Export').replace(/[\\/?*[\]:]/g, '').slice(0, 31) || 'Export';
  return writeExcelFile(data, {
    sheet: name,
    columns: (widths || []).map(width => ({ width })),
    stickyRowsCount: 1,
    dateFormat: 'yyyy-mm-dd',
  }).toBlob();
}

async function exportXlsx({ filename, title, columns, rows }) {
  // Lazy-loaded (see header note): nobody pays for the XLSX writer until
  // they click an XLSX export.
  const aoa = [
    columns.map(c => c.label),
    ...rows.map(r => columns.map(c => r[c.key])),
  ];
  // Reasonable column widths from the longer of label vs widest value.
  const widths = columns.map((c) => {
    const maxLen = Math.max(
      String(c.label).length,
      ...rows.map(r => String(r[c.key] ?? '').length),
    );
    return Math.min(40, Math.max(8, maxLen + 2));
  });
  const blob = await xlsxBlob({ sheet: title, rows: aoa, widths });
  triggerDownload(blob, `${SAFE_NAME(filename)}_${today()}.xlsx`);
}

async function exportPdf({ filename, title, columns, rows }) {
  // Dynamic imports keep the heavy jsPDF bundle out of the main chunk.
  // Import-shape note: depending on the bundler's CJS↔ESM interop,
  // jsPDF's constructor can live on either the module's `default` or
  // its named `jsPDF` export (raw Node ESM exposes the constructor as
  // the NAMED export and makes `default` a namespace object; Vite
  // often maps `default` to the constructor). Handle both so a build
  // change can't break the export at runtime. Same defensiveness for
  // autoTable (default vs named).
  const jspdfMod = await import('jspdf');
  const jsPDF = (typeof jspdfMod.default === 'function' ? jspdfMod.default : jspdfMod.jsPDF);
  if (typeof jsPDF !== 'function') {
    throw new Error('jsPDF constructor not found in module');
  }
  const autoTableModule = await import('jspdf-autotable');
  const autoTable = autoTableModule.default || autoTableModule.autoTable;
  if (typeof autoTable !== 'function') {
    throw new Error('jspdf-autotable function not found in module');
  }
  // Landscape gives more room for wide tables (typical for player logs).
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  // Header
  if (title) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(String(title), 40, 40);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text(`Generated ${new Date().toLocaleString()}`, 40, 56);
    doc.setTextColor(0);
  }
  autoTable(doc, {
    startY: title ? 72 : 40,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => {
      const v = r[c.key];
      return v == null ? '' : String(v);
    })),
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [46, 109, 180], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.align === 'right')   acc[i] = { halign: 'right'   };
      if (c.align === 'center')  acc[i] = { halign: 'center'  };
      return acc;
    }, {}),
    margin: { left: 40, right: 40 },
  });
  doc.save(`${SAFE_NAME(filename)}_${today()}.pdf`);
}

export function exportTable({ format, ...rest }) {
  if (format === 'xlsx')  return exportXlsx(rest);
  if (format === 'pdf')   return exportPdf(rest);
  return exportCsv(rest);
}
